import React, { useState, useEffect, useLayoutEffect } from 'react'
import { Button, FormControl, FormGroup, HelpBlock, Label, Radio } from 'react-bootstrap'
import ReactSelect, { Option } from 'react-select'

import i18n from 'live/services/i18n'

import { IUpdateModeFrontPresenter, PresenterProps } from 'plugin-codaf-risk-ext-bowtie/shared/UpdateModeFrontAPI'
import { IBarrier } from 'plugin-codaf-risk-ext-bowtie/types'
import { SwitchButton } from 'plugin-generic-components/shared/components/components'
import { HoldFunction } from 'plugin-quantitative-ruled-channel-dra-engine/types/enums'
import { isNullOrUndefined, isNullOrUndefinedOrEmpty } from 'plugin-quantitative-ruled-channel-dra-engine/utils'

import { listMappedChannels } from '../services/channels'
import { UpdateMode } from './style'

interface IChannelRule {
    threshold: number | string // this must be a number
    resultantPfd: number | string // this must be a number
}

// =====

interface IChannelType {
    id: string
    name: string
    isActive: boolean
}

interface IMappedChannelsInfo {
    channelId: string
    sourceId: string
    sourceConfig: ISourceConfig
}

interface ISourceConfig {
    provider: string
    expression: string
}

// =====

export interface IRuledChannelSetting {
    channelId?: string
    holdSetting?: string
    validationInterval?: {
        minValue: number | string // this must be a number
        maxValue: number | string // this must be a number
    }
    rules?: IChannelRule[]
    initialPFD: number | string // this must be a number
}

// =====

type ILiveApiBarrierConfig = {
    ruledChannelBarrierSetting: IRuledChannelSetting
    mappedChannels: IMappedChannelsInfo[]
}

type IMappedChannelsOptions = {
    label: string
    value: string
}

export const QuantitativeRuledChannelsApi: React.FC<PresenterProps> = ({ barrierPointers, onChanged, editable }) => {
    const [mappedChannelOptions, setMappedChannelOptions] = useState<IMappedChannelsOptions[]>([])
    const [holdFunctionSwitch, setHoldFunctionSwitch] = useState<boolean>(false) // TODO: setar a partir do json

    // PARSER WILL BE DONE ATE UPDATE MODE CONTROLLER

    const deleteAllScalations = (): void => {
        const newArray = []
        barrierPointers[0].escalations = [...newArray]
        // onChanged()
    }

    const removeEscalationsAndCreateEmptyJsonWithChannelSettingIfNotExist = (barriers: IBarrier[]): boolean => {
        if (!barriers[0]?.updateModeJsonConfig) {
            barriers.forEach(barPoint => {
                if (!barPoint.updateModeJsonConfig) {
                    barPoint.updateModeJsonConfig = {
                        channelId: null,
                        holdSetting: HoldFunction.NONE,
                        rules: [{ threshold: null, resultantPfd: null }],
                        validationInterval: { minValue: null, maxValue: null },
                        initialPFD: null
                    }
                }
            })
            deleteAllScalations()
            // onChanged()
        }

        return true
    }

    const getUpdateModeJsonConfig = (barrier: IBarrier): IRuledChannelSetting => {
        return barrier?.updateModeJsonConfig as IRuledChannelSetting
    }

    const handleRuledChannelBarrierSettingChange = (obj: Option<string>): void => {
        barrierPointers.forEach(bar => {
            if (!getUpdateModeJsonConfig(bar)?.channelId) {
                bar.updateModeJsonConfig = {
                    channelId: null,
                    holdSetting: HoldFunction.NONE,
                    rules: [{ threshold: null, resultantPfd: null }],
                    validationInterval: { minValue: null, maxValue: null },
                    initialPFD: null
                }
            }

            getUpdateModeJsonConfig(bar).channelId = obj.value
        })
        onChanged()
    }

    const handleHoldSwitchChange = () => {
        if (holdFunctionSwitch) {
            barrierPointers.forEach(bar => (getUpdateModeJsonConfig(bar).holdSetting = HoldFunction.NONE))
            onChanged()
        }
        setHoldFunctionSwitch(!holdFunctionSwitch)
    }

    const handleHoldRadioChange = (holdFunc: HoldFunction): void => {
        barrierPointers.forEach(bar => (getUpdateModeJsonConfig(bar).holdSetting = holdFunc))
        onChanged()
    }

    const handleDeleteRule = (index: number): void => {
        const newRulesArray = getUpdateModeJsonConfig(barrierPointers[0]).rules
        newRulesArray.splice(index, 1)
        barrierPointers.forEach(bar => {
            getUpdateModeJsonConfig(bar).rules = [...newRulesArray]
        })
        onChanged()
    }

    const handleAddNewRule = (): void => {
        const newRulesArray = getUpdateModeJsonConfig(barrierPointers[0]).rules
        const ruleToAdd: IChannelRule = {
            threshold: null,
            resultantPfd: null
        }

        newRulesArray.push(ruleToAdd)

        barrierPointers.forEach(bar => {
            getUpdateModeJsonConfig(bar).rules = [...newRulesArray]
        })

        onChanged()
    }

    const handleMappedChannelsFetch = async (): Promise<void> => {
        try {
            const data = await listMappedChannels(barrierPointers[0].asset.id)
            const formattedData = data.mappedChannelsDto.map(el => {
                const validOption: IMappedChannelsOptions = {
                    label: el.channelId,
                    value: el.channelId
                }

                return validOption
            })
            setMappedChannelOptions(formattedData)
        } catch (e) {
            console.log(e)
        }
    }

    const checkAndRemoveEscalations = (barriers: IBarrier[]): void => {
        if (barriers) {
            barriers.forEach(bar => {
                bar.escalations = []
            })
        }

        onChanged()
    }

    useEffect(() => {
        if (barrierPointers) {
            handleMappedChannelsFetch()
            checkAndRemoveEscalations(barrierPointers)
        }
    }, [barrierPointers[0]?.updateModeCode])

    return (
        removeEscalationsAndCreateEmptyJsonWithChannelSettingIfNotExist(barrierPointers) && (
            <UpdateMode>
                <FormGroup validationState={!barrierPointers[0]?.updateModeJsonConfig ? 'error' : 'success'}>
                    <Label required className="control-label channels-label">
                        {i18n('Channel')} *
                    </Label>

                    {editable ? (
                        <ReactSelect
                            options={mappedChannelOptions}
                            searchable={true}
                            clearable={false}
                            id="barrierEditor-barrierEditModal-field-channel"
                            onChange={handleRuledChannelBarrierSettingChange}
                            value={{
                                value: getUpdateModeJsonConfig(barrierPointers[0])?.channelId,
                                label: getUpdateModeJsonConfig(barrierPointers[0])?.channelId
                            }}
                        />
                    ) : (
                        <FormControl type="text" value={getUpdateModeJsonConfig(barrierPointers[0])?.channelId} />
                    )}
                </FormGroup>
                <FormGroup
                    validationState={
                        isNullOrUndefinedOrEmpty(
                            getUpdateModeJsonConfig(barrierPointers[0])?.validationInterval?.maxValue
                        ) ||
                        isNullOrUndefinedOrEmpty(
                            getUpdateModeJsonConfig(barrierPointers[0])?.validationInterval?.minValue
                        )
                            ? 'error'
                            : 'success'
                    }
                >
                    <Label className="channels-label">{i18n('Validation Range')}</Label>
                    <div className="validation-range-formgroup">
                        <div className="validation-range-formgroup-labeltext">
                            <span className="validation-label">{i18n('Value min')}</span>
                            <FormControl
                                readOnly={!editable}
                                type="number"
                                onChange={e => {
                                    const newThresholdArray = getUpdateModeJsonConfig(barrierPointers[0]).rules[0]

                                    newThresholdArray.threshold = (e.target as HTMLInputElement).value

                                    barrierPointers.forEach(el => {
                                        getUpdateModeJsonConfig(
                                            el
                                        ).validationInterval.minValue = (e.target as HTMLInputElement).value
                                        getUpdateModeJsonConfig(el).rules[0] = {
                                            ...newThresholdArray
                                        }
                                    })
                                    onChanged()
                                }}
                                value={getUpdateModeJsonConfig(barrierPointers[0])?.validationInterval?.minValue}
                            />
                        </div>
                        <div className="validation-range-formgroup-labeltext">
                            <span className="validation-label">{i18n('Value max')}</span>

                            <FormControl
                                readOnly={!editable}
                                type="number"
                                onChange={e => {
                                    barrierPointers.forEach(
                                        el =>
                                            (getUpdateModeJsonConfig(
                                                el
                                            ).validationInterval.maxValue = (e.target as HTMLInputElement).value)
                                    )
                                    onChanged()
                                }}
                                value={getUpdateModeJsonConfig(barrierPointers[0])?.validationInterval?.maxValue}
                            />
                        </div>
                        <span className="units">{i18n('Units')}</span>
                    </div>
                    <HelpBlock className="validation-range-help-block">
                        {i18n('Values out of this range will not be considered.')}
                    </HelpBlock>
                </FormGroup>
                <div className="hold-function-wrapper">
                    <SwitchButton
                        disabled={!editable}
                        id={'holdFunction'}
                        label={'Hold Function'}
                        value={holdFunctionSwitch}
                        onChange={handleHoldSwitchChange}
                    />
                </div>
                {holdFunctionSwitch && (
                    <div className="radio-container">
                        <Radio
                            disabled={!editable}
                            name="radioGroup"
                            onChange={() => handleHoldRadioChange(HoldFunction.MIN)}
                        >
                            {i18n('Minimum')}
                        </Radio>
                        <Radio
                            disabled={!editable}
                            name="radioGroup"
                            onChange={() => handleHoldRadioChange(HoldFunction.MAX)}
                        >
                            {i18n('Maximum')}
                        </Radio>
                    </div>
                )}
                <FormGroup
                    bsClass="initial-pfd-formgroup"
                    validationState={
                        isNullOrUndefinedOrEmpty(getUpdateModeJsonConfig(barrierPointers[0])?.initialPFD) ||
                        getUpdateModeJsonConfig(barrierPointers[0])?.initialPFD > 1
                            ? 'error'
                            : 'success'
                    }
                >
                    <Label className="channels-label">{i18n('Initial PFD (Probability of Failure on Demand)')}</Label>
                    <FormControl
                        readOnly={!editable}
                        type="number"
                        min={0}
                        max={1}
                        onChange={e => {
                            barrierPointers.forEach(
                                el => (getUpdateModeJsonConfig(el).initialPFD = (e.target as HTMLInputElement).value)
                            )
                            onChanged()
                        }}
                        value={getUpdateModeJsonConfig(barrierPointers[0])?.initialPFD}
                    />
                </FormGroup>
                <Label className="channels-label rule-label"> {i18n('Rules')}</Label>
                {getUpdateModeJsonConfig(barrierPointers[0])?.rules?.map((rule, index) => {
                    return (
                        <FormGroup
                            key={index}
                            className="new-rules-formgroups"
                            validationState={
                                isNullOrUndefinedOrEmpty(
                                    getUpdateModeJsonConfig(barrierPointers[0]).rules[index].threshold
                                ) ||
                                isNullOrUndefinedOrEmpty(
                                    getUpdateModeJsonConfig(barrierPointers[0]).rules[index].resultantPfd
                                ) ||
                                getUpdateModeJsonConfig(barrierPointers[0]).rules[index].resultantPfd > 1
                                    ? 'error'
                                    : 'success'
                            }
                        >
                            <div className="new-rule-formgroup">
                                <div className="new-rule-formgroup-labeltext">
                                    <span className="new-rule-labels">{i18n('Value min')}</span>
                                    <FormControl
                                        type="number"
                                        value={getUpdateModeJsonConfig(barrierPointers[0]).rules[index].threshold}
                                        disabled={true}
                                    />
                                </div>
                                <div className="new-rule-formgroup-labeltext">
                                    <span className="new-rule-labels">{i18n('Value max')}</span>
                                    <FormControl
                                        type="number"
                                        onChange={e => {
                                            barrierPointers.forEach(
                                                el =>
                                                    (getUpdateModeJsonConfig(el).rules[
                                                        index + 1
                                                    ].threshold = (e.target as HTMLInputElement).value)
                                            )
                                            onChanged()
                                        }}
                                        value={
                                            !getUpdateModeJsonConfig(barrierPointers[0]).rules[index + 1]
                                                ? getUpdateModeJsonConfig(barrierPointers[0])?.validationInterval
                                                      ?.maxValue
                                                : getUpdateModeJsonConfig(barrierPointers[0]).rules[index + 1].threshold
                                        }
                                        disabled={
                                            !editable || !getUpdateModeJsonConfig(barrierPointers[0])?.rules[index + 1]
                                        }
                                    />
                                </div>
                                <div className="equals">
                                    <span> = </span>
                                </div>

                                <div className="new-rule-formgroup-labeltext">
                                    <span className="new-rule-labels">{i18n('PFD')}</span>
                                    <FormControl
                                        readOnly={!editable}
                                        type="number"
                                        onChange={e => {
                                            barrierPointers.forEach(
                                                el =>
                                                    (getUpdateModeJsonConfig(el).rules[
                                                        index
                                                    ].resultantPfd = (e.target as HTMLInputElement).value)
                                            )
                                            onChanged()
                                        }}
                                        value={rule.resultantPfd}
                                    />
                                </div>
                                {editable && getUpdateModeJsonConfig(barrierPointers[0]).rules.length > 1 ? (
                                    <i
                                        className="far fa-trash-alt rules-trash"
                                        onClick={() => handleDeleteRule(index)}
                                    ></i>
                                ) : (
                                    <i className="far fa-trash-alt rules-trash disabled"></i>
                                )}
                            </div>
                        </FormGroup>
                    )
                })}

                <Button
                    className="new-rule-button"
                    bsStyle="primary"
                    onClick={() => {
                        handleAddNewRule()
                    }}
                    disabled={!editable}
                >
                    <i className="fas fa-plus"></i> {i18n('New Rule')}
                </Button>
            </UpdateMode>
        )
    )
}
